<template>
    <div class="">
        <install-wizard-card>
            <template #top-header>
                Alarm Profiles
            </template>
            <template #top-content>
                <p>
                    These profiles set what ranges selected points will alarm on.
                </p>
                <p>
                    The Points for each profile will be selected later, however now you can edit profiles to the values
                    that are ideal for your use and select which profiles you want to use later.
                </p>
            </template>

            <template #body-summary>
                <div class="flex flex-wrap ml-6">
                    <div class="w-full sm:w-3/4 mr-0 sm:mr-4">Alarm Profiles can be modified later, but this will help you be prepared to onboard new points of monitoring.</div>
                    <a class="mt-4 sm:mt-0">
                        <span @click="saveAll" class="pb-1 text-blue-800 border-b border-blue-800 cursor-pointer">Save Progress</span>
                    </a>
                </div>
                <neutral-message v-if="isSaving">Saving</neutral-message>
                <error-message v-if="errorSaving">Error deleting group. Try again later</error-message>
            </template>

            <template #body-action>
                <h4>Profiles</h4>
                <!--
                    Built off of Tailwind UI - Data Display -> Stats -> With Shared Borders
                    URL: https://tailwindui.com/components/application-ui/data-display/stats#component-72704cac437a06d94cdb941c274591ba
                 -->
                <div v-for="(profile, profileIndex) in profiles" class="grid grid-cols-1 bg-white overflow-hidden md:grid-cols-4 sm:border-t sm:border-gray-200">
                    <!-- Activate Profile -->
                    <div class="">
                        <div class="flex flex-row justify-around py-2">
                            <span   class="px-2 my-2 rounded-full"
                                    :class="[profile.active ? 'bg-green-100' : 'bg-red-100']">
                                {{ profile.active ? 'Enabled' : 'Disabled' }}
                            </span>
                            <div class="flex justify-center my-2">
                                <toggle class=""
                                        v-model:value="profiles[profileIndex].active"
                                        @toggle="profiles[profileIndex].isEditing = false"
                                ></toggle>
                            </div>
                        </div>

                        <div    class="flex flex-row justify-around ml-4 py-2">
                            <!-- Threshold Alarms -->
                            <div    class="text-center">
                                <!-- Text when editing -->
                                <div    v-if="profile.isEditing"
                                        class="flex flex-col justify-around">
                                    <div>Threshold Alarming?</div>
                                    <div    class="my-1">
                                        ({{ profile.thresholdAlarmingEnabled ? 'Enabled' : 'Disabled' }})</div>
                                    <div>
                                        <toggle     v-model:value="profiles[profileIndex].thresholdAlarmingEnabled"
                                        ></toggle>
                                    </div>
                                </div>

                                <!-- Text when not editing -->
                                <div    v-else
                                        class="flex flex-col">
                                    <span>Threshold Alarming</span>
                                    <span>
                                        {{ profile.thresholdAlarmingEnabled ? 'Enabled' : 'Disabled' }}
                                    </span>
                                </div>
                            </div>

                            <!-- Offline Alarms -->
                            <div    class="text-center">
                                <!-- Tet when editing -->
                                <div    v-if="profile.isEditing"
                                        class="flex flex-col justify-around">
                                    <div>Offline Alarming?</div>
                                    <div    class="my-1">
                                        ({{ profile.offlineAlarmingEnabled ? 'Enabled' : 'Disabled' }})</div>
                                    <div    class="">
                                        <toggle     v-model:value="profiles[profileIndex].offlineAlarmingEnabled"
                                        ></toggle>
                                    </div>
                                </div>

                                <!-- Text when not editing -->
                                <div    v-else
                                        class="flex flex-col">
                                    <span>Offline Alarming</span>
                                    <span>
                                        {{ profile.offlineAlarmingEnabled ? 'Enabled' : 'Disabled' }}
                                    </span>
                                </div>

                            </div>
                        </div>
                    </div>

                    <!-- Profile Name -->
<!--                    <div class="md:col-span-2">-->
                    <div class="">
                        <div class="px-4 py-5 sm:p-6">
                            <dl>
                                <div v-if="profile.isEditing" class="">
                                    <floating-input     :id="'profile_name_' + profileIndex"
                                                        v-model:value="profiles[profileIndex].name"
                                                        label="Profile Name"
                                    ></floating-input>
                                </div>
                                <dt v-else
                                        class="text-base leading-6 font-normal text-gray-900">
                                    {{ profile.name }}
                                </dt>
                                <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
                                    <div class="flex items-baseline text-sm leading-8 font-semibold text-grey-100">
                                        {{ intervalText(profile) }} before an alarm is generated.
                                    </div>
                                </dd>
                                <div v-if="profile.isEditing"
                                        class="mt-2">
                                    <toggle-radio
                                            @radio-clicked="updateInterval(profileIndex)"
                                            :options="intervalOptions"
                                            v-model="profiles[profileIndex].intervalString">

                                    </toggle-radio>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <!-- Alarm Limits -->
                    <div class="mt-2">
                        <div class="px-4 py-5 sm:p-6 flex justify-center">
                            <dl>
                                <dd class="mb-4 flex justify-center items-baseline md:block lg:flex">
                                    <div class="inline-flex items-baseline px-2.5 py-0.5 rounded-full text-lg font-medium leading-5 bg-red-100 text-red-800 md:mt-2 lg:mt-0">
                                        <svg class="-ml-1 mr-0.5 shrink-0 self-center h-5 w-5 text-red-500"
                                             fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd"
                                                  d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                                                  clip-rule="evenodd"/>
                                        </svg>
                                        <input v-if="profile.isEditing"
                                               v-model="profiles[profileIndex].high"
                                               class="w-3/4 border rounded p-2 leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700"
                                               step="any"
                                               type="number">
                                        <div v-else>
                                            <span class="sr-only">
                                                Upper Limit
                                            </span>
                                            {{ (profile.high || profile.high === 0) ? profile.high : '-' }}
                                        </div>

                                    </div>
                                </dd>
                                <dd class="mt-4 flex justify-around items-baseline md:block lg:flex">
                                    <div class="inline-flex items-baseline px-2.5 py-0.5 rounded-full text-lg font-medium leading-5 bg-blue-100 text-blue-800 md:mt-2 lg:mt-0">
                                        <svg class="-ml-1 mr-0.5 shrink-0 self-center h-5 w-5 text-blue-500"
                                             fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd"
                                                  d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                                                  clip-rule="evenodd"/>
                                        </svg>
                                        <input v-if="profile.isEditing"
                                               v-model="profiles[profileIndex].low"
                                               class="w-3/4 border rounded p-2 leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700"
                                               step="any"
                                               type="number">
                                        <div v-else>
                                            <span class="sr-only">
                                                    Lower Limit
                                                </span>
                                            {{ (profile.low || profile.low === 0) ? profile.low : '-' }}
                                        </div>
                                    </div>
                                </dd>
                            </dl>
                        </div>
                    </div>

                    <!-- Edit & Duplicate -->
                    <div class="mt-4">
                        <div class="flex justify-center align-content-space-between grid">
                            <button v-if="profile.active"
                                    type="button"
                                    @click="editProfile(profileIndex)"
                                    class="block items-center my-2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm font-sans rounded-md text-blue-700 bg-white-600 hover:bg-blue-200 focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-300"
                                    :disabled="!profile.active">
                                {{ profile.isEditing ? 'Stop Editing' : 'Edit Profile' }}
                            </button>
                            <button v-else
                                    type="button"
                                    class="block items-center my-2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm font-sans rounded-md text-white-700 bg-gray-300 hover:bg-gray-300 focus:outline-none focus:ring-blue-200 focus:border-blue-400"
                                    :disabled="!profile.active">
                                Edit Profile
                            </button>
                            <div class="flex justify-center my-2 align-content-space-around">
                                <button type="button"
                                        @click="duplicateProfile(profileIndex)"
                                        class="text-blue-800 border-b border-blue-800 cursor-pointer">
                                    Duplicate
                                </button>
                                <span @click="deleteProfile(profileIndex)"
                                      class="lnr icon-circle-minus ml-2 text-red-600" />
                            </div>

                        </div>
                    </div>
                </div>
                <neutral-message v-if="isSaving">Saving</neutral-message>
            </template>

            <template #bottom-header>
                Done with alarm profiles?
            </template>

            <template #bottom-button>
                <a>
                    <button type="button"
                            @click="completeStep"
                            class="relative inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:border-blue-700 active:bg-blue-700">
                        <slot name="bottom-button-text">Complete and move on!</slot>
                    </button>
                </a>
            </template>
            <!--            <template #bottom-button-text></template>-->
        </install-wizard-card>
    </div>
</template>

<script>
    import InstallWizardCard from "./InstallWizardCard";
    import Toggle from "@/components/elements/toggle";
    import ToggleRadio from "@/components/elements/ToggleRadio";
    import axios from "axios";
    import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";

    export default {
        name: "InstallWizardAlarmProfiles",
        props: {
        },
        components: {ToggleRadio, Toggle, InstallWizardCard},
        mixins: [SonicuCoreMixin],
        data() {
            let intervalToMinutes = {
                'Default': 35,
                '1 Hour': 60,
                '2 Hours': 120,
            }
            return {
                isSaving: false,
                errorSaving: false,
                numberSaved: 0,
                profiles: [],
                intervalOptions: Object.keys(intervalToMinutes),
                intervalConversion: intervalToMinutes,
            }
        },
        mounted() {
            axios.get(this.company.buildCompanyApiInternalRoute('wizard/alarm-profiles')).then(
                response => {
                    response.data.forEach((item, key) => {
                        this.profiles.push({
                            id: item.id,
                            name: item.name,
                            interval: item.interval / 60,
                            intervalString: item.intervalString,
                            high: item.high,
                            low: item.low,
                            active: item.active,
                            thresholdAlarmingEnabled: item.thresholdAlarmingEnabled,
                            offlineAlarmingEnabled: item.offlineAlarmingEnabled,
                            isEditing: false,
                        })
                    })
                }
            )
        },
        methods: {
            editProfile(profileIndex) {
                this.profiles.forEach((item, index) => {
                    if (index !== profileIndex) {
                        this.profiles[index].isEditing = false
                    }
                })
                this.profiles[profileIndex].isEditing = !this.profiles[profileIndex].isEditing
            },
            updateInterval(profileIndex) {
                this.profiles[profileIndex].interval = this.intervalConversion[this.profiles[profileIndex].intervalString]
            },
            intervalText(profile) {
                let interval = profile.interval
                if (interval < 60) {
                    return `${interval} minute${this.pluralString(interval)}`
                }
                else if (interval % 60 === 0) {
                    let hours = interval / 60
                    return `${hours} hour${this.pluralString(hours)}`
                }
                else {
                    let hours = Math.floor(interval / 60)
                    let minutes = interval % 60
                    return `${hours} hour${this.pluralString(hours)} & ${minutes} minute${this.pluralString(minutes)}`
                }
            },
            duplicateProfile(profileIndex) {
                let suffix = ' Copy'
                let newProfile = JSON.parse(JSON.stringify(this.profiles[profileIndex]))
                newProfile.name = newProfile.name + suffix
                newProfile.active = true
                newProfile.id = 0
                this.profiles.push(newProfile)
            },
            saveProfile(profileIndex) {
                axios.post(this.company.buildCompanyApiInternalRoute('wizard/alarm-profiles'),
                    {
                        data: this.profiles[profileIndex],
                    }
                ).then(
                    response => {
                        this.numberSaved++
                        if (this.numberSaved === this.profiles.length) {
                            this.isSaving = false
                            this.numberSaved = 0
                        }
                    }
                ).catch(
                    error => {
                        this.errorSaving = true
                        this.isSaving = false
                    }
                )

            },
            saveAll() {
                this.isSaving = true
                axios.post(this.company.buildCompanyApiInternalRoute('wizard/alarm-profiles'),
                    {
                        data: this.profiles,
                    }
                ).then(
                    response => {
                            this.isSaving = false
                    }
                ).catch(
                    error => {
                        this.errorSaving = true
                        this.isSaving = false
                    }
                )
            },
            deleteProfile(profileIndex) {
                let backupProfile = JSON.parse(JSON.stringify(this.profiles[profileIndex]))
                this.profiles.splice(profileIndex, 1)
                axios.delete(this.company.buildCompanyApiInternalRoute('wizard/alarm-profiles') + '/' + backupProfile.id).then(
                    response => {
                    }
                ).catch(
                    error => {
                        this.profiles.push(backupProfile)
                        this.errorSaving = true
                    }
                )
            },
            completeStep() {
                this.saveAll()
                axios.post(this.targetRoute, { complete: true}).then(function (response) {
                    window.location.href = response.data.target;
                    return true;
                })
            },
            pluralString(num) {
                return (num > 1) ? 's' : ''
            },
            toggleThresholdAlarming(profileIndex) {
                this.profiles[profileIndex].thresholdAlarmingEnabled = !this.profiles[profileIndex].thresholdAlarmingEnabled
            },
            toggleOfflineAlarming(profileIndex) {
                this.profiles[profileIndex].offlineAlarmingEnabled = !this.profiles[profileIndex].offlineAlarmingEnabled
            },
        },
        computed: {
            targetRoute: function () {
                if (this.specialRoute) {
                    return this.specialRoute;
                } else {
                    return window.location.pathname;
                }
            },
        }
    }
</script>

<style scoped>

</style>